import { useEffect } from 'react';

import { Spinner } from '@change/ds-core-ui';

import { TranslatePlural } from '@change-corgi/core-react/i18n';
import { VisuallyHidden } from '@change-corgi/design-system-legacy/a11y';

import { Waypoint } from 'src/app/shared/components/scroll';
import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';

import type { Comment } from '../../types';

import { CommentCard } from './components/CommentCard';
import { useCommentsList } from './hooks/useCommentsList';
import { useTrackCommentsImpression } from './hooks/useTrackCommentsImpression';

const isScrolledToBottom = (el: HTMLElement, threshold = 16): boolean =>
	el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;

export const CommentsList: React.FC<Props> = ({
	setCommentsVisibility,
	comments,
	hasNextPage,
	nextPageLoading,
	loadMore,
	...props
}) => {
	const {
		actions: { loadMore: loadMoreWithTracking },
	} = useCommentsList({ comments, loadMore });

	const { containerRef, loadingRef, recordListEvent, lastEvent } = useLazyLoadedListFocus();

	useEffect(() => {
		if (!comments.length) {
			setCommentsVisibility(false);
		}
	}, [comments.length, setCommentsVisibility]);

	const trackCommentsImpression = useTrackCommentsImpression(containerRef, comments.length > 0);

	return (
		<div ref={containerRef} {...props} data-qa="petition-comments-card-body-list-container">
			<Waypoint onEnter={trackCommentsImpression} />
			<ol
				className="flex max-h-[357px] flex-1 flex-col gap-4 overflow-auto rounded-b-2xl border-0 pt-5 backdrop-blur-sm md:pt-6"
				sx={{
					backdropFilter: 'blur(8px)',
					WebkitBackdropFilter: 'blur(8px)',
					maskImage: 'linear-gradient(to top, transparent 0%, black 22%)',
					WebkitMaskImage: 'linear-gradient(to top, transparent 0%, black 22%)',
				}}
				data-testid="comments-list"
				onScroll={(event) => {
					const target = event.currentTarget;
					if (isScrolledToBottom(target) && hasNextPage && !nextPageLoading) {
						recordListEvent({ type: 'loadMore', itemsCount: comments.length, triggerFocusFallback: true });
						loadMoreWithTracking();
					}
				}}
			>
				{comments.map((comment, index) => (
					<li key={comment.id}>
						{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
							<VisuallyHidden role="alert">
								<TranslatePlural
									value="corgi.petition.comments.fetched_more"
									count={comments.length - lastEvent.previousItemsCount}
									replacements={{ count: comments.length - lastEvent.previousItemsCount }}
								/>
							</VisuallyHidden>
						)}
						<CommentCard comment={comment} />
					</li>
				))}
				{nextPageLoading && <Spinner className="mx-auto" ref={loadingRef} />}
				<div className="-mt-4 min-h-8 md:min-h-12" />
			</ol>
		</div>
	);
};

type Props = Readonly<{
	setCommentsVisibility: (visible: boolean) => void;
	comments: readonly Comment[];
	hasNextPage: boolean;
	nextPageLoading: boolean;
	loadMore: () => void;
}>;
